import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalLoading } from 'components/ModalLoading';
import { RequestStatus } from 'features/request/requestSlice';
import type { ActKc2Data } from 'gql/graphql';
import { ActKc2StatusEnum, ChapterEnum, ContractStatusEnum, CostGroupEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useActKC2Download } from 'hooks/useActKC2Download';
import { ActKc2Title } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { getActKC2StatusLabel } from 'pages/Project/Common/types';
import { ActKc2PricesAndTotals } from 'pages/Project/UerProject/WorkOrder/ActKc2PricesAndTotals/ActKc2PricesAndTotals';
import { ActKC2Copy } from 'pages/Project/UerProject/WorkOrder/AddActKC2/ActKC2Copy';
import { ActKC2Delete } from 'pages/Project/UerProject/WorkOrder/AddActKC2/ActKC2Delete';
import { EditActKC2Modal } from 'pages/Project/UerProject/WorkOrder/AddActKC2/EditActKC2Modal';
import { InflationDistributionModal } from 'pages/Project/UerProject/WorkOrder/InflationDistributionModal/InflationDistributionModal';
import type { BadgePropSize, Position } from 'uikit';
import { Tooltip } from 'uikit';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';
import { getElementCoordinatesBySelector, useNodeByRefCallback } from 'utils/dom';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDropdownContextMenuTooltipController } from '../../hooks/useDropdownContextMenuTooltipController';
import { useMethodsConfirmationDialog } from '../../hooks/useMethodsConfirmationDialog';
import { EditActKc2Correction } from '../../pages/Project/UerProject/WorkOrder/EditActKC2Correction/EditActKc2Correction';
import { useUerCreateActKC2Correction } from '../../pages/Project/UerProject/WorkOrder/EditActKC2Correction/hooks/useUerCreateActKC2Correction';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { DropdownContext } from '../DropdownContext';

import Style from './KebabActKC2.module.css';

interface IKebabActKC2Props {
    id: number;
    status: ActKc2StatusEnum;
    statusWorkOrder?: WorkOrderStatusEnum;
    date?: Date;
    number?: number | string | null;
    kebabBadgeSize?: BadgePropSize;
    statusContract?: ContractStatusEnum;
    isDisabledByProjectStatus?: boolean;
    onRefresh?: () => void;
    chapter: ChapterEnum;
    workOrderSubObjectId: number;
    seasonId?: number;
    isInactive?: boolean | null;
    isCorrection?: boolean | null;
    canCreateCorrection?: boolean | null;
    isSKzSAct?: boolean | null;
    withHighLightManager?: (action?: () => void) => void;
    skzsPanelRefresh?: () => void;
    costGroup?: CostGroupEnum;
}

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');
const contextMenuButtonKey = 'ListItem';

const editItemLabel = 'Редактировать реквизиты';
const inflationDistrItemLabel = 'Распределение инфляции для позиций МТР';
const copyItemLabel = 'Копировать';
const downloadItemLabel = 'Выгрузить Акт КС-2';
const createItemLabel = 'Создать корректировочный Акт';
const deleteItemLabel = 'Удалить';
const viewItemLabel = 'Просмотреть Акт КС-2';

export const KebabActKC2: React.FC<IKebabActKC2Props> = (props) => {
    const navigate = useNavigate();
    const ref = useRef(null);
    const { versionId } = useParams();
    const isVersionPage = !!versionId;

    const {
        id,
        status,
        date,
        number,
        kebabBadgeSize,
        statusContract,
        statusWorkOrder,
        isDisabledByProjectStatus,
        chapter,
        workOrderSubObjectId,
        onRefresh,
        isInactive,
        isCorrection,
        canCreateCorrection,
        isSKzSAct,
        withHighLightManager,
        skzsPanelRefresh,
        costGroup,
    } = props;

    const [isOpenEditKC2, setIsOpenEditKC2] = useState<boolean>(false);
    const [isOpenInflation, setIsOpenInflation] = useState<boolean>(false);
    const [tooltipPosition, setTooltipPosition] = useState<Position>(undefined);
    const [correctionToEdit, setCorrectionToEdit] = useState<ActKc2Data | undefined>();
    const [correctionIdToEdit, setCorrectionIdToEdit] = useState<number | undefined>();
    const [isOpenViewKC2, setIsOpenViewKC2] = useState<boolean>(false);

    const [copyButton, copyRef] = useNodeByRefCallback(contextMenuButtonKey);
    const [deleteButton, deleteRef] = useNodeByRefCallback(contextMenuButtonKey);

    const { aktKC2: aktKC2Rules } = useAccess({
        statusKC2: status,
    });

    const {
        isSubmittedDownload,
        actKC2AsExcelData,
        actKC2AsExcelStatus,
        actKC2AsExcelError,
        handleActKC2AsExcelAbort,
        handleDownloadActKC2,
        handleCloseModalErrorDownload,
        handleCreateReportDownload,
    } = useActKC2Download(id);

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const isStatusWorkOrderDelayed = statusWorkOrder === WorkOrderStatusEnum.Delayed;
    const isStatusWorkOrderCompleted = statusWorkOrder === WorkOrderStatusEnum.Completed;
    const isStatusWorkOrderInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;
    const isDisabledByWorkOrderStatus = isStatusWorkOrderDelayed || isStatusWorkOrderCompleted;

    const handleOpenEditKC2 = useCallback(() => {
        if (isCorrection) {
            setCorrectionIdToEdit(id);
        } else {
            setIsOpenEditKC2(true);
        }
    }, [id, isCorrection]);
    const handleOpenInflationDistribution = useCallback(() => setIsOpenInflation(true), []);

    const onCreateCorrectionSuccess = useCallback((data?: ActKc2Data) => {
        setCorrectionToEdit(data);
    }, []);

    const uerCreateActKC2Correction = useUerCreateActKC2Correction({
        primaryActKC2Id: id,
        onSuccess: onCreateCorrectionSuccess,
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: uerCreateActKC2Correction,
        });

    const handleCloseInflationDistribution = useCallback(() => {
        setIsOpenInflation(false);
    }, []);

    const handleCloseEditKC2 = useCallback(() => {
        setIsOpenEditKC2(false);
    }, []);

    const isOpenViewKC2Switch = useCallback(() => setIsOpenViewKC2(R.not), []);

    const handleOnRefresh = useCallback(() => {
        if (isOpenEditKC2) {
            skzsPanelRefresh?.();
        }

        setIsOpenEditKC2(false);
        setIsOpenInflation(false);
        onRefresh?.();
    }, [isOpenEditKC2, onRefresh, skzsPanelRefresh]);

    const handleItemOnClick = useCallback(
        (item) => () => {
            const action = getItemOnClick(item) as () => void;

            if (action) {
                if (withHighLightManager) {
                    withHighLightManager();
                }
                action();
            }
        },
        [withHighLightManager],
    );

    const onEditActKc2CorrectionClose = useCallback(
        (hasSaved: boolean | undefined) => {
            if (correctionToEdit) {
                onRefresh?.();

                const navState = {
                    reload: Math.random(),
                    actNavInfo: {
                        actId: correctionToEdit.id,
                    },
                };

                setTimeout(() => navigate('./', { replace: true, state: navState }));
            } else if (hasSaved) {
                onRefresh?.();
            }
            setCorrectionToEdit(undefined);
            setCorrectionIdToEdit(undefined);
        },
        [correctionToEdit, navigate, onRefresh],
    );

    const kebabMenuItems = useMemo(() => {
        const editItem = {
            label: editItemLabel,
            action: handleOpenEditKC2,
            active: true,
        };

        const viewItem = {
            label: viewItemLabel,
            action: isOpenViewKC2Switch,
            active: true,
        };
        const inflationDistrItem = {
            label: inflationDistrItemLabel,
            active: true,
            action: handleOpenInflationDistribution,
        };

        const copyItem = {
            label: copyItemLabel,
            active: false,
        };

        const downloadItem = {
            label: downloadItemLabel,
            active: true,
            action: handleDownloadActKC2,
        };

        const createTooltip = {
            haveTooltip: true,
            tooltipName: 'NoCorrectionTooltip',
            tooltipMsg: 'Текущий Акт КС-2 не включен в состав Справки КС-3. Создание корректировки невозможно',
        };

        const createItem = {
            label: createItemLabel,
            active: true,
            action: handleOpenConfirmationDialog,
            ...(status === ActKc2StatusEnum.Accepted && !isInactive && !canCreateCorrection ? createTooltip : {}),
        };

        const deleteItem = {
            label: deleteItemLabel,
            active: false,
        };

        return isCorrection
            ? [
                  editItem,
                  viewItem,
                  ...(costGroup !== CostGroupEnum.Others ? [inflationDistrItem] : []),
                  downloadItem,
                  ...(status === ActKc2StatusEnum.Accepted ? [createItem] : []),
                  deleteItem,
              ]
            : [
                  editItem,
                  viewItem,
                  ...(costGroup !== CostGroupEnum.Others ? [inflationDistrItem] : []),
                  copyItem,
                  downloadItem,
                  ...(status === ActKc2StatusEnum.Accepted ? [createItem] : []),
                  deleteItem,
              ];
    }, [
        handleOpenEditKC2,
        isOpenViewKC2Switch,
        handleOpenInflationDistribution,
        handleDownloadActKC2,
        handleOpenConfirmationDialog,
        status,
        isInactive,
        canCreateCorrection,
        isCorrection,
        costGroup,
    ]);

    const isLoadingDownload = useMemo(() => actKC2AsExcelStatus === RequestStatus.FETCHING, [actKC2AsExcelStatus]);

    const getDisabled = useCallback(
        (item) =>
            (item.label === editItemLabel && !aktKC2Rules.canEditActKC2) ||
            (item.label === inflationDistrItemLabel && chapter !== ChapterEnum.Chapter1_7) ||
            (item.label === inflationDistrItemLabel && !aktKC2Rules.canEditActKC2) ||
            (item.label === inflationDistrItemLabel && isInactive) ||
            (item.label === copyItemLabel && !aktKC2Rules.canCreate) ||
            (item.label === copyItemLabel && isDisabledByWorkOrderStatus) ||
            (item.label === copyItemLabel && (isInactive || isCorrection)) ||
            (item.label === downloadItemLabel && !aktKC2Rules.canUnload) ||
            (item.label !== downloadItemLabel && !isContractStatusWork) ||
            (item.label !== downloadItemLabel && isDisabledByProjectStatus) ||
            (item.label === createItemLabel && status !== ActKc2StatusEnum.Accepted) ||
            (item.label === createItemLabel && !aktKC2Rules.canCreate) ||
            (item.label === createItemLabel && !isStatusWorkOrderInProgress) ||
            (item.label === createItemLabel && isInactive) ||
            (item.label === createItemLabel && !canCreateCorrection) ||
            (item.label === deleteItemLabel && status !== ActKc2StatusEnum.InProgress) ||
            (item.label === deleteItemLabel && !aktKC2Rules.canDelete) ||
            (item.label === deleteItemLabel && isDisabledByWorkOrderStatus),
        [
            aktKC2Rules.canCreate,
            aktKC2Rules.canDelete,
            aktKC2Rules.canEditActKC2,
            aktKC2Rules.canUnload,
            canCreateCorrection,
            chapter,
            isContractStatusWork,
            isCorrection,
            isDisabledByProjectStatus,
            isDisabledByWorkOrderStatus,
            isInactive,
            isStatusWorkOrderInProgress,
            status,
        ],
    );

    const isDisableCopyRef =
        !aktKC2Rules.canCreate || isDisabledByProjectStatus || isDisabledByWorkOrderStatus || !isContractStatusWork;

    const isDisableDeleteRef =
        !aktKC2Rules.canDelete ||
        status !== ActKc2StatusEnum.InProgress ||
        isDisabledByProjectStatus ||
        isDisabledByWorkOrderStatus ||
        !isContractStatusWork;

    const renderLeftSide = useCallback(
        (item: any) => {
            const refs = {
                [copyItemLabel]: isDisableCopyRef ? null : copyRef,
                [deleteItemLabel]: isDisableDeleteRef ? null : deleteRef,
            } as any;

            return refs[item.label] ? <div ref={refs[item.label]} /> : undefined;
        },
        [copyRef, deleteRef, isDisableCopyRef, isDisableDeleteRef],
    );

    const isAktInProgress = status === ActKc2StatusEnum.InProgress;

    useEffect(() => {
        if (!isAktInProgress || !aktKC2Rules.canDelete) {
            const onMouseOver = () => {
                const pos = getElementCoordinatesBySelector(deleteButton);

                setTooltipPosition({
                    ...pos,
                    y: (pos?.y || 0) + ((deleteButton as any)?.clientHeight || 0) / 2,
                } as any);
            };
            const onMouseOut = () => setTooltipPosition(undefined);

            (deleteButton as any)?.addEventListener('mouseover', onMouseOver);
            (deleteButton as any)?.addEventListener('mouseout', onMouseOut);

            return () => {
                (deleteButton as any)?.removeEventListener('mouseover', onMouseOver);
                (deleteButton as any)?.removeEventListener('mouseout', onMouseOut);
            };
        }

        return undefined;
    }, [deleteButton, status, aktKC2Rules, isAktInProgress]);

    const {
        getMenuItemAttributes,
        tooltipPosition: menuTooltipPosition,
        activeTooltipMsg,
        onMenuMouseMove,
        onMenuMouseLeave,
    } = useDropdownContextMenuTooltipController();

    const withCloseHelper = useCallback(
        (action: (arg?: boolean) => void) => {
            if (withHighLightManager) {
                return (arg?: boolean) =>
                    withHighLightManager(() => {
                        if (arg) {
                            action(arg);
                        } else {
                            action();
                        }
                    });
            }

            return action;
        },
        [withHighLightManager],
    );

    return (
        <>
            <div className={Style.kebabActKc2}>
                {kebabBadgeSize && (
                    <ActKc2Title act={{ status } as ActKc2Data} size={kebabBadgeSize} badgeStyle={{ marginRight: 8 }} />
                )}
                <DropdownContext
                    offset={8}
                    getItemDisabled={getDisabled}
                    items={kebabMenuItems as any}
                    getItemLabel={getItemLabel}
                    anchorRef={ref}
                    direction="downStartLeft"
                    getItemOnClick={handleItemOnClick}
                    getItemLeftSide={renderLeftSide}
                    getItemAttributesHandler={getMenuItemAttributes}
                    onMouseMoveHandler={onMenuMouseMove}
                    onMouseLeaveHandler={onMenuMouseLeave}
                    className={Style.kebabActKc2MenuItem}
                    isDisabled={isVersionPage}
                />
                {activeTooltipMsg && (
                    <Tooltip
                        size="s"
                        direction="upCenter"
                        position={menuTooltipPosition}
                        isInteractive={false}
                        style={{ zIndex: 2 }}
                    >
                        {activeTooltipMsg}
                    </Tooltip>
                )}
                <ActKC2Copy
                    actKc2Id={id}
                    number={number}
                    date={date ? new Date(date) : undefined}
                    buttonNode={copyButton}
                    hasNoButton
                    onCopy={onRefresh}
                    withCloseHelper={withCloseHelper}
                />
                {isAktInProgress && aktKC2Rules.canDelete && (
                    <ActKC2Delete
                        actKC2Id={id}
                        costGroup={costGroup}
                        number={number}
                        date={date ? new Date(date) : undefined}
                        onDelete={handleOnRefresh}
                        status={status}
                        buttonNode={deleteButton}
                        hasNoButton
                        withCloseHelper={withCloseHelper}
                    />
                )}
                {isOpenEditKC2 && (
                    <EditActKC2Modal
                        actKC2Id={id}
                        isSKzSAct={isSKzSAct}
                        onClose={withCloseHelper(handleCloseEditKC2)}
                        onRefresh={handleOnRefresh}
                        aktKC2Rules={aktKC2Rules}
                        statusWorkOrder={statusWorkOrder}
                    />
                )}
                {isOpenInflation && (
                    <InflationDistributionModal
                        actKC2Id={id}
                        number={number}
                        onClose={withCloseHelper(handleCloseInflationDistribution)}
                        onRefresh={handleOnRefresh}
                        aktKC2Rules={aktKC2Rules}
                        statusWorkOrder={statusWorkOrder}
                        isOpened={isOpenInflation}
                        chapter={chapter}
                        workOrderSubObjectId={workOrderSubObjectId}
                    />
                )}
                {(correctionToEdit || correctionIdToEdit) && (
                    <EditActKc2Correction
                        actKc2={correctionToEdit}
                        actKC2Id={correctionIdToEdit}
                        onClose={withCloseHelper(onEditActKc2CorrectionClose)}
                        aktKC2Rules={aktKC2Rules}
                        statusWorkOrder={statusWorkOrder}
                    />
                )}
                {isOpenViewKC2 && <ActKc2PricesAndTotals onClose={isOpenViewKC2Switch} actKC2Id={id} />}
                <Tooltip size="s" position={tooltipPosition} direction="leftCenter">
                    <div style={{ color: '#EEF8FB' }}>
                        {isAktInProgress && !aktKC2Rules.canDelete
                            ? `У вас недостаточно прав для удаления Актa${descriptionNumberAndDate(number, date)}`
                            : `Невозможно удаление Акта в статусе «${getActKC2StatusLabel(status)}»`}
                    </div>
                </Tooltip>
            </div>
            <ErrorHandlingModal
                titleText="Выгрузка Акта КС-2"
                isSubmitted={isSubmittedDownload}
                statusRequest={actKC2AsExcelStatus}
                dataRequest={actKC2AsExcelData?.uerExportActKC2AsExcel}
                errorsRequest={actKC2AsExcelError}
                handleCloseModalError={handleCloseModalErrorDownload}
                handleCreateReport={handleCreateReportDownload}
            />
            {isLoadingDownload && <ModalLoading title="Выгрузка Акта КС-2" onClose={handleActKC2AsExcelAbort} />}
            <ConfirmationDialog
                textHeader="Создание корректировочного акта"
                textConfirmation={
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px' }}>
                        <div style={{ marginBottom: '16px' }}>Вы уверены, что хотите создать корректировочный Акт?</div>
                        <div>
                            После создания корректировочного Акта текущий Акт КС-2 не будет участвовать в расчете
                            Накопительной по НЗ и Договора.
                        </div>
                    </div>
                }
                cancelButtonText="Отмена"
                confirmButtonText="Создать"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

KebabActKC2.displayName = 'KebabActKC2';
